import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, type ApolloError } from "@apollo/client";
import type { ExpandedState, Updater } from "@tanstack/react-table";
import Table, { TableControls, TableFooter, TableHeading } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import usePagination from "@/hooks/usePagination";
import { sendToRaygun } from "@/lib/raygunUtils";
import { CALL_TOPIC_CALLS_QUERY } from "../queries";
import { interactionPathForCall } from "../_utils";
import { getColumns, type CallRow } from "./CallsTableColumns";
import CallDetails from "./CallDetails";

type Props = {
  startDateTime: string;
  endDateTime: string;
  direction: string | null;
  answered: boolean | null;
  workerName: string | null;
  topics: string[];
  topicsReady: boolean;
  hasActiveFilters: boolean;
};

const CALLS_HEADING = "Calls";
const CALLS_BODY = "Every call in range, newest first. Expand a row for details.";

export default function CallsTable({
  startDateTime,
  endDateTime,
  direction,
  answered,
  workerName,
  topics,
  topicsReady,
  hasActiveFilters
}: Props) {
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination({
    defaultPageSize: 100
  });
  const topicsKey = useMemo(() => [...topics].sort().join("\n"), [topics]);
  const [expandedCallSid, setExpandedCallSid] = useState<string | null>(null);
  const expanded = useMemo<ExpandedState>(
    () => (expandedCallSid ? { [expandedCallSid]: true } : {}),
    [expandedCallSid]
  );

  // Accordion: one row open at a time, like the old card. Funnels every
  // expansion change (row click, mobile expand button) through the single sid.
  const handleExpandedChange = useCallback(
    (updater: Updater<ExpandedState>) => {
      const next = typeof updater === "function" ? updater(expanded) : updater;
      if (next === true) return;
      const open = Object.keys(next).filter((key) => next[key]);
      const added = open.filter((key) => key !== expandedCallSid);
      setExpandedCallSid(added[0] ?? (expandedCallSid && open.includes(expandedCallSid) ? expandedCallSid : null));
    },
    [expanded, expandedCallSid]
  );

  useEffect(() => {
    resetPagination();
  }, [startDateTime, endDateTime, direction, answered, workerName, topicsKey, resetPagination]);

  const { data, previousData, loading, error, refetch } = useQuery(CALL_TOPIC_CALLS_QUERY, {
    variables: {
      startDateTime,
      endDateTime,
      direction,
      answered,
      workerName,
      topics,
      ...paginationVariables
    },
    skip: !topicsReady,
    notifyOnNetworkStatusChange: true,
    onError: (queryError: ApolloError) => {
      sendToRaygun(queryError, { tags: ["call-topics", "calls"] });
    }
  });

  const connection = (data ?? previousData)?.callTopicCalls;
  const rows: CallRow[] = useMemo(
    () => (connection?.nodes ?? []).map((node) => ({ ...node, id: node.callSid })),
    [connection]
  );
  const columns = useMemo(() => getColumns(), []);

  if (error && !connection) {
    return (
      <>
        <TableHeading heading={CALLS_HEADING} body={CALLS_BODY} />
        <EmptyState
          title="Unable to load calls"
          caption="Try refreshing the page. If the problem continues, contact support."
          iconName="error"
          action={{ children: "Try Again", onClick: () => refetch() }}
        />
      </>
    );
  }

  return (
    <>
      <TableHeading heading={CALLS_HEADING} body={CALLS_BODY} />
      <Table
        data={rows}
        columns={columns}
        loading={loading || !topicsReady}
        expandedState={expanded}
        onExpandedChange={handleExpandedChange}
        onClickRow={(row) => row.toggleExpanded()}
        renderExpandedRow={({ row }) => (
          <CallDetails
            callSid={row.original.callSid}
            hasRecording={row.original.recordingUrl != null}
            personName={row.original.personName}
            personPgaId={row.original.personPgaId}
            callerPhone={row.original.callerPhone}
            interactionPath={interactionPathForCall(row.original)}
          />
        )}
        hasActiveFilters={hasActiveFilters}
        renderNoDataEmptyState={() => <EmptyState title="No calls in this range." iconName="call" />}
        renderFilteredEmptyState={() => <EmptyState title="No calls match these filters." iconName="call" />}
        renderControls={() => <TableControls pagination={createHeaderPagination(connection, { loading })} />}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(connection, { loading })}
            totalRowCount={connection?.totalCount ?? rows.length}
          />
        )}
        data-testid="call-topic-calls-table"
      />
    </>
  );
}
